/**
 * validateRecurring.js
 * --------------------------------------------------
 * Front‑end helper that validates the **shape** *and* **semantics** of a
 * `recurring` object before you generate recurring dates on the client.
 *
 * A valid object has this structural type (all `camelCase`):
 *
 * ```ts
 * type Recurring = {
 *   enabled: boolean;
 *   option: 'WEEKLY' | 'MONTHLY';
 *   // WEEKLY only
 *   every?: string[];          // weekday names
 *   num_weeks?: number;        // ≥ 1
 *   // MONTHLY only
 *   monthly_option?: 'ON_DATE' | 'ON_FIRST' | 'ON_LAST';
 *   monthly_every?: number | string; // day 1‑31 *or* weekday string
 *   num_months?: number;       // ≥ 1
 * };
 * ```
 *
 * The validator is kept **framework‑agnostic** so you can plug it into React,
 * Vue, plain JS, etc. It returns a `{ ok, errors }` result; surface the
 * errors however you like (toast, form helper‑text, console, …).
 *
 * --------------------------------------------------
 * Usage example
 * --------------------------------------------------
 *
 * ```ts
 * import validateRecurring from './validateRecurring.js';
 *
 * const { ok, errors } = validateRecurring(formState.recurring);
 * if (!ok) {
 *   console.error(errors.join('\n'));
 *   // prevent submit …
 * }
 * ```
 */

const WEEKDAYS = [
  'monday', 'tuesday', 'wednesday', 'thursday',
  'friday', 'saturday', 'sunday',
];

/* ───────────────────────── helpers ────────────────────────── */

const isInteger       = (v) => Number.isInteger(v);
const isPositiveInt   = (v) => isInteger(v) && v > 0;
const normalizeDay    = (s) => typeof s === 'string' ? s.toLowerCase().trim() : undefined;
const isWeekdayString = (s) => WEEKDAYS.includes(normalizeDay(s));

/* ───────────────────────── validator ──────────────────────── */

export function validateRecurring(recurring) {
  const errors = [];

  // Basic type check --------------------------------------------------------
  if (!recurring || typeof recurring !== 'object') {
    return { ok: false, errors: ['Recurring value must be an object'] };
  }

  const { enabled } = recurring;
  if (typeof enabled !== 'boolean') {
    errors.push('"enabled" must be a boolean');
  }
  if (!enabled) return { ok: errors.length === 0, errors }; // nothing else to check

  /* 1. OPTION ------------------------------------------------------------- */
  const option = recurring.option;
  if (option !== 'WEEKLY' && option !== 'MONTHLY') {
    errors.push('"option" must be "WEEKLY" or "MONTHLY"');
    return { ok: false, errors }; // further checks depend on a valid option
  }

  /* 2. WEEKLY ------------------------------------------------------------- */
  if (option === 'WEEKLY') {
    const { num_weeks, every } = recurring;

    if (!isPositiveInt(num_weeks)) {
      errors.push('"num_weeks" must be a positive integer (≥ 1)');
    }

    if (!Array.isArray(every) || every.length === 0) {
      errors.push('"every" must be a non-empty array of weekday names');
    } else {
      const invalid = every.filter((w) => !isWeekdayString(w));
      if (invalid.length) {
        errors.push(`Unknown weekday names in "every": ${invalid.join(', ')}`);
      }
    }
  }

  /* 3. MONTHLY ------------------------------------------------------------ */
  if (option === 'MONTHLY') {
    const {
      num_months,
      monthly_option,
      monthly_every,
    } = recurring;

    if (!isPositiveInt(num_months)) {
      errors.push('"num_months" must be a positive integer (≥ 1)');
    }

    if (!['ON_DATE', 'ON_FIRST', 'ON_LAST'].includes(monthly_option)) {
      errors.push('"monthly_option" must be "ON_DATE", "ON_FIRST" or "ON_LAST"');
      return { ok: false, errors }; // remaining checks depend on this
    }

    if (monthly_option === 'ON_DATE') {
      if (!isPositiveInt(monthly_every) || monthly_every < 1 || monthly_every > 31) {
        errors.push('For "ON_DATE", "monthly_every" must be an integer 1-31');
      }
    }

    if (monthly_option === 'ON_FIRST' || monthly_option === 'ON_LAST') {
      if (!isWeekdayString(monthly_every)) {
        errors.push(`For "${monthly_option}", "monthly_every" must be a weekday name`);
      }
    }
  }

  return { ok: errors.length === 0, errors };
}

export default validateRecurring;